/**
 * WS-H subject-scoped consents (design `ws-h-consent-design.md` §3.1/§6, PFL-2569 MR9).
 *
 * Mirrors PhotoQa BE `PhotoQa.Domain.Entities.ConsentPurpose` / `SubjectConsentResponse` /
 * `UpdateSubjectConsentRequest` / `OtpChallengeResponse` (be-app `develop`,
 * `src/Services/PhotoQa/PhotoQa.Contracts/Rest/{Requests,Responses}`), used by:
 *   - `GET /api/v1/me/consents` / `GET /api/v1/orgs/{orgId}/consents` — one
 *     `SubjectConsentResponse` per {@link ConsentPurpose}, defaulting to not-granted.
 *   - `PUT /api/v1/me/consents/{purpose}` — any authenticated user, own subject, no OTP.
 *   - `PUT /api/v1/orgs/{orgId}/consents/{purpose}` — studio OWNER (or SUPER_ADMIN) only,
 *     OTP-gated for `FaceAnalytics`/`ModelTraining`.
 *   - `POST /api/v1/orgs/{orgId}/consents/{purpose}/otp-challenge` — step 1 of the OTP flow.
 */

/** The four D24 consent buckets (`ConsentPurpose` enum, be-app `PhotoQa.Domain.Entities`). */
export type ConsentPurpose =
    | 'Terms'
    | 'AiProcessing'
    | 'FaceAnalytics'
    | 'ModelTraining';

export const CONSENT_PURPOSES: readonly ConsentPurpose[] = [
    'Terms',
    'AiProcessing',
    'FaceAnalytics',
    'ModelTraining',
];

/**
 * Purposes that require the OTP-confirm step when granted at the STUDIO level
 * (`PUT /orgs/{orgId}/consents/{purpose}`, design §6 bullet 2 / Q44). Never required for the
 * user's own `/me/consents` path (design §6 bullet 1: "No OTP").
 */
export const OTP_REQUIRED_ORG_PURPOSES: readonly ConsentPurpose[] = [
    'FaceAnalytics',
    'ModelTraining',
];

/** `ConsentPolicyResponse` — `GET /api/v1/me/consents/policy`: the consent-text version the BE
 *  currently accepts. Every grant must echo it; a stored grant on any other version is stale. */
export interface IConsentPolicyResponse {
    policyVersion: string;
}

/** 400 from any consent PUT whose `policyVersion` is not the current one
 *  (`errorDetails.policyVersion` = current). The client refetches the policy and re-prompts. */
export const CONSENT_POLICY_VERSION_OUTDATED =
    'CONSENT_POLICY_VERSION_OUTDATED';

/**
 * A grant recorded against an older policy version no longer counts (the BE gate answers
 * `FACE_RECOGNITION_CONSENT_REQUIRED` for it), so the subject must accept the current text again.
 */
export function needsReSign(
    consent: Pick<ISubjectConsentResponse, 'granted' | 'policyVersion'>,
    currentPolicyVersion: string | null
): boolean {
    return (
        consent.granted &&
        currentPolicyVersion !== null &&
        consent.policyVersion !== currentPolicyVersion
    );
}

/** The two sign-up acknowledgements the first-login gate checks (`/register` and the
 *  ConsentGate dialog show both). */
export const SIGNUP_CONSENT_PURPOSES: readonly ConsentPurpose[] = [
    'Terms',
    'AiProcessing',
];

/**
 * True when BOTH sign-up purposes are granted under `policyVersion` — matching the
 * iam-portal `hasCurrentAcceptance` (kanban #3306). A stale `policyVersion` or a
 * missing/withdrawn row means the gate must re-prompt.
 */
export function hasCurrentAcceptance(
    consents: ReadonlyArray<Pick<ISubjectConsentResponse, 'purpose' | 'granted' | 'policyVersion'>>,
    policyVersion: string
): boolean {
    return SIGNUP_CONSENT_PURPOSES.every((purpose) =>
        consents.some(
            (c) =>
                c.purpose.toLowerCase() === purpose.toLowerCase() &&
                c.granted &&
                c.policyVersion === policyVersion
        )
    );
}

/** `SubjectConsentResponse` — response for the `GET .../consents` list endpoints and both
 *  `PUT .../consents/{purpose}` endpoints. */
export interface ISubjectConsentResponse {
    subjectType: 'User' | 'Org' | string;
    subjectId: string;
    purpose: ConsentPurpose | string;
    granted: boolean;
    policyVersion?: string | null;
    acceptedAt?: string | null;
    withdrawnAt?: string | null;
    otpVerified: boolean;
}

/** `UpdateSubjectConsentRequest` — request body for both `PUT .../consents/{purpose}` endpoints. */
export interface IUpdateSubjectConsentRequest {
    granted: boolean;
    policyVersion?: string | null;
    /** WS-H MR4 — required (with `otpCode`) only for a studio grant of FaceAnalytics/ModelTraining. */
    otpChallengeId?: string | null;
    otpCode?: string | null;
}

/** `OtpChallengeResponse` — response for `POST /orgs/{orgId}/consents/{purpose}/otp-challenge`. */
export interface IOtpChallengeResponse {
    challengeId: string;
    expiresAt: string;
    maskedTarget: string;
}
